/**
 * Which site an address is (decided by Yehuda 2026-10-26). The portal is one Static Web
 * App under several names, and the name picks what its home page is:
 *
 *   portal.focalpointcareco.com   the Focal Point Care landing page (src/landing/): public,
 *                                 no sign-in, one card per company, each handing off to
 *                                 that company's own portal
 *   every other name              the FCC portal, as it is (portal.fcc-corp.com, localhost;
 *                                 apps.focalpointcareco.com redirects to the landing host)
 *
 * The landing host is the landing page and nothing else (Yehuda, 2026-10-28): its other
 * paths (/hub, /admin/apps, ...) go to the same path on the FCC portal's own name
 * (`fccRedirect`), so old links keep working but the FCC portal never opens under the
 * Focal Point Care name. Nor does a sign-in finish there: none starts there.
 * QRM's and Guided Care's portals get a name of their own here when they exist.
 *
 * Pure, so the tests can run it. On a dev server `?site=landing` shows the landing page.
 */
export type Site = 'landing' | 'fcc'

export const LANDING_HOSTS = ['portal.focalpointcareco.com'] as const

/** The FCC portal's own name. */
export const FCC_PORTAL_ORIGIN = 'https://portal.fcc-corp.com'

type Loc = { hostname: string; pathname: string; search: string; hash?: string }

const isHome = (loc: Loc) => loc.pathname === '/' || loc.pathname === ''
const isLandingHost = (loc: Loc) => (LANDING_HOSTS as readonly string[]).includes(loc.hostname.toLowerCase())

export function siteFor(loc: Loc, dev = false): Site {
  if (isLandingHost(loc)) return 'landing'
  if (dev && isHome(loc) && new URLSearchParams(loc.search).get('site') === 'landing') return 'landing'
  return 'fcc'
}

/** On the landing host, any path but the home page: the same address on the FCC portal. */
export function fccRedirect(loc: Loc): string | null {
  if (!isLandingHost(loc) || isHome(loc)) return null
  return FCC_PORTAL_ORIGIN + loc.pathname + loc.search + (loc.hash ?? '')
}
